import {
  ArrowLeft,
  CalendarClock,
  Edit,
  Gauge,
  Loader2,
  Pause,
  Play,
  Plus,
  Timer,
  Trash2,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { TriggerFormModal } from '@/components/ui/trigger-form-modal';
import { useEquipmentItem } from '@/features/equipment/queries';
import { useEquipmentType } from '@/features/equipment-types/queries';
import { useVessels } from '@/features/vessels/queries';
import { vesselDisplayName } from '@/features/vessels/types';
import {
  useCreatePlanTrigger,
  useDeleteMaintenancePlan,
  useDeletePlanTrigger,
  useMaintenancePlan,
  usePlanTriggers,
  useSetMaintenancePlanActive,
  useSetPlanTriggerActive,
} from '@/features/maintenance-plans/queries';
import {
  describeTrigger,
  planTriggerKindColor,
  type MaintenancePlan,
  type PlanTrigger,
} from '@/features/maintenance-plans/types';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

function Field({
  label,
  value,
  fullWidth = false,
}: {
  label: string;
  value: React.ReactNode;
  fullWidth?: boolean;
}) {
  const display =
    value === null || value === undefined || value === '' ? '—' : value;
  return (
    <div className={cn(fullWidth && 'sm:col-span-2')}>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 text-sm">{display}</dd>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid gap-4 sm:grid-cols-2">{children}</dl>
      </CardContent>
    </Card>
  );
}

function formatDateTime(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function triggerIcon(kind: PlanTrigger['kind']) {
  switch (kind) {
    case 'time':
      return CalendarClock;
    case 'counter':
      return Timer;
    case 'cbm':
      return Gauge;
  }
}

/**
 * TriggersCard — lists all triggers attached to a plan.
 *
 * Each row shows:
 *   - colored kind badge (sky/purple/amber)
 *   - the human-readable schedule from describeTrigger()
 *   - lastFiredAt (or "never fired" placeholder)
 *   - per-trigger pause/resume + delete buttons
 *
 * The form to create new triggers ships in F6.3 — this card is
 * read+manage only. The buttons stay enabled for paused triggers
 * so the user can re-activate them.
 */
function TriggersCard({
  planId,
  triggers,
}: {
  planId: string;
  triggers: PlanTrigger[];
}) {
  const { t } = useTranslation();
  const setActiveMut = useSetPlanTriggerActive(planId);
  const deleteMut = useDeletePlanTrigger(planId);
  const createMut = useCreatePlanTrigger(planId);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const createErrorMessage = (() => {
    if (!createMut.error) return null;
    if (createMut.error instanceof ApiError) {
      return t(createMut.error.key, {
        defaultValue: t('maintenancePlan.triggerForm.saveError'),
        ...(createMut.error.params ?? {}),
      });
    }
    return t('maintenancePlan.triggerForm.saveError');
  })();

  return (
    <>
      <div className="mb-3 flex justify-end">
        <Button
          size="sm"
          onClick={() => {
            createMut.reset();
            setAddOpen(true);
          }}
        >
          <Plus aria-hidden />
          {t('maintenancePlan.triggerForm.addButton')}
        </Button>
      </div>

      {triggers.length === 0 ? (
        <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
          {t('maintenancePlan.triggers.empty')}
        </div>
      ) : (
        <ul className="space-y-2">
        {triggers.map((tr) => {
          const Icon = triggerIcon(tr.kind);
          return (
            <li
              key={tr.id}
              className={cn(
                'flex flex-wrap items-center gap-3 rounded-md border p-3 text-sm',
                !tr.isActive && 'opacity-60',
              )}
            >
              <span
                className={cn(
                  'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
                  planTriggerKindColor(tr.kind),
                )}
              >
                <Icon className="h-4 w-4" aria-hidden />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-medium">
                    {t(`maintenancePlan.triggerKind.${tr.kind}`)}
                  </span>
                  <span className="text-muted-foreground">
                    {describeTrigger(tr)}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {tr.lastFiredAt
                    ? t('maintenancePlan.triggers.lastFiredAt', {
                        when: formatDateTime(tr.lastFiredAt),
                      })
                    : t('maintenancePlan.triggers.neverFired')}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setActiveMut.mutate({
                      triggerId: tr.id,
                      isActive: !tr.isActive,
                    })
                  }
                  disabled={setActiveMut.isPending}
                >
                  {tr.isActive ? (
                    <Pause aria-hidden />
                  ) : (
                    <Play aria-hidden />
                  )}
                  {tr.isActive
                    ? t('maintenancePlan.triggers.pause')
                    : t('maintenancePlan.triggers.resume')}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setPendingDelete(tr.id)}
                  disabled={deleteMut.isPending}
                >
                  <Trash2 aria-hidden />
                </Button>
              </div>
            </li>
          );
        })}
        </ul>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        title={t('maintenancePlan.triggers.deleteTitle')}
        description={t('maintenancePlan.triggers.deleteDescription')}
        confirmLabel={t('common.delete')}
        variant="destructive"
        loading={deleteMut.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          deleteMut.mutate(pendingDelete, {
            onSuccess: () => setPendingDelete(null),
          });
        }}
      />

      <TriggerFormModal
        open={addOpen}
        onOpenChange={(open) => {
          if (!open) {
            setAddOpen(false);
            createMut.reset();
          }
        }}
        loading={createMut.isPending}
        errorMessage={createErrorMessage}
        onConfirm={(payload) => {
          createMut.mutate(payload, {
            onSuccess: () => {
              setAddOpen(false);
              createMut.reset();
            },
          });
        }}
      />
    </>
  );
}

function DetailContent({ plan }: { plan: MaintenancePlan }) {
  const { t } = useTranslation();
  const { data: vessels } = useVessels();
  const { data: equipment } = useEquipmentItem(plan.equipmentId ?? undefined);
  const { data: equipmentType } = useEquipmentType(
    plan.equipmentTypeId ?? undefined,
  );

  const vesselName = useMemo(() => {
    if (!plan.vesselId) return null;
    const v = vessels?.find((x) => x.id === plan.vesselId);
    return v ? vesselDisplayName(v) : null;
  }, [vessels, plan.vesselId]);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Section title={t('maintenancePlan.detail.sectionIdentity')}>
        <Field label={t('maintenancePlan.fields.title')} value={plan.title} />
        <Field
          label={t('maintenancePlan.fields.description')}
          value={
            plan.description ? (
              <p className="whitespace-pre-line">{plan.description}</p>
            ) : null
          }
          fullWidth
        />
      </Section>

      <Section title={t('maintenancePlan.detail.sectionTarget')}>
        <Field
          label={t('maintenancePlan.fields.equipmentId')}
          value={
            plan.equipmentId && equipment ? (
              <Link
                to={`/equipment/${equipment.id}`}
                className="text-primary hover:underline"
              >
                <span className="font-mono">{equipment.tag}</span> — {equipment.name}
              </Link>
            ) : null
          }
        />
        <Field
          label={t('maintenancePlan.fields.equipmentTypeId')}
          value={
            plan.equipmentTypeId && equipmentType
              ? equipmentType.name
              : null
          }
        />
        <Field
          label={t('maintenancePlan.fields.vesselId')}
          value={
            vesselName ? (
              <Link
                to={`/vessels/${plan.vesselId}`}
                className="text-primary hover:underline"
              >
                {vesselName}
              </Link>
            ) : (
              t('maintenancePlan.allVessels')
            )
          }
        />
      </Section>

      <Section title={t('maintenancePlan.detail.sectionTemplate')}>
        <Field
          label={t('maintenancePlan.fields.type')}
          value={t(`workOrder.type.${plan.type}`)}
        />
        <Field
          label={t('maintenancePlan.fields.priority')}
          value={t(`workOrder.priority.${plan.priority}`)}
        />
        <Field
          label={t('maintenancePlan.fields.estimatedHours')}
          value={plan.estimatedHours}
        />
        <Field
          label={t('maintenancePlan.fields.leadTimeDays')}
          value={`${plan.leadTimeDays} d`}
        />
        <Field
          label={t('maintenancePlan.fields.lastGeneratedAt')}
          value={formatDateTime(plan.lastGeneratedAt)}
        />
      </Section>
    </div>
  );
}

export function MaintenancePlanDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { data: plan, isLoading, isError, error, refetch } =
    useMaintenancePlan(id);
  const { data: triggers, isLoading: triggersLoading } = usePlanTriggers(id);
  const setActiveMut = useSetMaintenancePlanActive(id ?? '');
  const deleteMut = useDeleteMaintenancePlan();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const errorMessage = (() => {
    if (!isError) return null;
    if (error instanceof ApiError) {
      return t(error.key, {
        defaultValue: t('maintenancePlan.detail.loadError'),
        ...(error.params ?? {}),
      });
    }
    return t('maintenancePlan.detail.loadError');
  })();

  const onConfirmDelete = () => {
    if (!id) return;
    deleteMut.mutate(id, {
      onSuccess: () => {
        setConfirmOpen(false);
        navigate('/maintenance-plans');
      },
    });
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button
            asChild
            variant="ghost"
            size="icon"
            aria-label={t('common.back')}
          >
            <Link to="/maintenance-plans">
              <ArrowLeft aria-hidden />
            </Link>
          </Button>
          <div className="flex flex-wrap items-baseline gap-2">
            <h1 className="text-2xl font-semibold">
              {plan ? plan.title : t('common.loading')}
            </h1>
            {plan && (
              <span
                className={cn(
                  'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
                  plan.isActive
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-slate-100 text-slate-700',
                )}
              >
                {plan.isActive
                  ? t('maintenancePlan.active')
                  : t('maintenancePlan.paused')}
              </span>
            )}
          </div>
        </div>
        {plan && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => setActiveMut.mutate(!plan.isActive)}
              disabled={setActiveMut.isPending}
            >
              {plan.isActive ? <Pause aria-hidden /> : <Play aria-hidden />}
              {plan.isActive
                ? t('maintenancePlan.detail.pause')
                : t('maintenancePlan.detail.resume')}
            </Button>
            <Button asChild variant="outline">
              <Link to={`/maintenance-plans/${plan.id}/edit`}>
                <Edit aria-hidden />
                {t('common.edit')}
              </Link>
            </Button>
            <Button
              variant="destructive"
              onClick={() => setConfirmOpen(true)}
              disabled={deleteMut.isPending}
            >
              {deleteMut.isPending ? (
                <Loader2 className="animate-spin" aria-hidden />
              ) : (
                <Trash2 aria-hidden />
              )}
              {t('common.delete')}
            </Button>
          </div>
        )}
      </header>

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>{t('maintenancePlan.detail.loadError')}</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>{errorMessage}</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              {t('common.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {isLoading && (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      )}

      {plan && (
        <>
          <DetailContent plan={plan} />

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('maintenancePlan.detail.sectionTriggers')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {triggersLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 2 }).map((_, i) => (
                    <Skeleton key={i} className="h-14 w-full" />
                  ))}
                </div>
              ) : (
                <TriggersCard planId={plan.id} triggers={triggers ?? []} />
              )}
            </CardContent>
          </Card>
        </>
      )}

      {plan && (
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title={t('maintenancePlan.delete.title')}
          description={t('maintenancePlan.delete.description', {
            title: plan.title,
          })}
          confirmLabel={t('maintenancePlan.delete.confirm')}
          variant="destructive"
          loading={deleteMut.isPending}
          onConfirm={onConfirmDelete}
        />
      )}
    </div>
  );
}
